import Logger from 'Logger';
import Axios from 'helpers/Interceptor'
import { BrandContent, RecordDetails, RecordSelectorType, SpotifyRecordSearchResponse } from 'modules/Types';
import * as Sentry from "@sentry/react";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { FormattedWebsiteData } from './websiteApi';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/** 
 * Publishes brand content to create/update a website
 * @param brandContent - Content and configuration for the website
 * @returns Response data from the publish API
 */
export const publishBrandContent_api = async (
  brandContent: FormattedWebsiteData
) => {
  try {
    const response = await Axios.post('/brand-content/publish/website', {
      brandContent,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Updates external service links for brand content
 * @param brandSlug - Slug identifier for the brand
 * @param recordSlug - Slug identifier for the specific content
 * @param links - Object containing service name and URL to link
 * @returns Response data from the update API
 */
export const updateBrandContentExternalLinks_api = async (
  brandSlug: string,
  recordSlug: string,
  links: { serviceName: string, url: string }
) => {
  try {
    const results = await Axios.post(`/brand-content/${brandSlug}/${recordSlug}/link`, links)

    return results.data
  } catch (error) {
    throw error
  }
}

/**
 * Searches Spotify for music content matching search text
 * @param value - Search text to query Spotify
 * @param limit - Maximum number of results to return (default: 10)
 * @returns Spotify search results if successful
 */
export const searchSpotifyByText_api = async (
  value: string,
  limit: number = 10
): Promise<SpotifyRecordSearchResponse | undefined> => {
  try {
    const url = 'spotify/search/music'
    const encodedValue = encodeURIComponent(value)
    const options = { params: { q: encodedValue, limit: limit } }
    const response = await Axios.get<SpotifyRecordSearchResponse>(url, options)
    if (response?.data) return response.data
  } catch (e: unknown) {
    console.error('Error on searchSpotifyByText component: ', e)
  }
}

/**
 * Fetches available markets for a piece of content
 * @param contentSlug - Slug identifier for the content
 * @returns Array of available markets or empty array if none found
 */
export const fetchMarkets_api = async (
  contentSlug: string
): Promise<any> => {
  try {
    const response = await Axios.post<any>(`/brand-content/available-markets/${contentSlug}`);
    if (!response?.data?.markets) return [];
    return response.data.markets;
  } catch (error) {
    Logger.error(error);
    console.error('Error on fetchMarkets component: ', error);
    return [];
  }
};

/**
 * Gets detailed record information from Spotify
 * @param spotifyId - Spotify ID of the track/record
 * @param recordType - Type of record being requested
 * @param brandId - Optional ID of brand to associate with record
 * @returns Formatted record details including metadata and URLs
 */
export const getRecordDetails_api = async (
  spotifyId: string,
  recordType: RecordSelectorType,
  brandId?: number,
): Promise<RecordDetails | null> => {
  Sentry.setContext('getRecordDetails', { brandId: brandId, spotifyId: 'spotifyId' });

  try {
    const response = await Axios.get<BrandContent>(`/brand-content/${brandId}/track/${spotifyId}`, {
      params: {
        type: recordType
      }
    });

    const data = response?.data;

    if (data) {
      const { content_metadata, slug, name, url, id } = data;
      const spotifyReleaseType = content_metadata.type === "record"
        ? 'Single'
        : (content_metadata.spotify_release_type || content_metadata.type || 'Release');

      const recordDetails: RecordDetails = {
        ...content_metadata,
        id,
        full_info: true,
        slug,
        name,
        url,
        thumbnail_url: content_metadata.thumbnail_url,
        spotify_release_type: spotifyReleaseType,
        prid: content_metadata.prid
      };


      return recordDetails;
    }

    return null
  } catch (error) {
    Logger.error(error);
    console.error("Error on getRecordDetails component: ", error);
    return null
  }
}

/**
 * Retrieves content for a specific brand and content combination
 * @param brandSlug - Slug identifier for the brand
 * @param contentSlug - Slug identifier for the specific content
 * @returns Brand content data if found, null otherwise
 */
export const getBrandContent_api = async (
  brandSlug: string | undefined,
  contentSlug: string | undefined
) => {
  if (!brandSlug || !contentSlug) {
    return null;
  }

  try {
    const getData = await Axios.get<BrandContent>(`/brand-content/${brandSlug}/${contentSlug}`)
    if (getData.data) {
      return getData.data
    }
  } catch (error) {
    console.log("Error getting Brand content", error)
    return null
  }
}

/** Interface for presave daily stats response */
interface PresaveDailyStats {
  date: string;
  count: number;
}

/** Interface for demographics data */
interface DemographicsData {
  age_ranges: {
    range: string;
    count: number;
  }[];
  genders: {
    gender: string;
    count: number;
  }[];
}

/** Interface for audience member */
interface AudienceMember {
  id: number;
  email: string;
  name?: string;
  country?: string;
  city?: string;
  presaveDate: string;
}

/** Interface for geography stats */
interface GeographyStats {
  location: string;
  count: number;
  percentage: number;
}

/** Interface for brand content analytics */
interface BrandContentAnalytics {
  totalPresaves: number;
  dailyStats: PresaveDailyStats[];
  // Add other analytics fields as needed
}

/**
 * Fetches analytics data for a brand's content
 * @param brandId - ID of the brand
 * @returns Analytics data if successful, null otherwise
 */
export const getBrandContentAnalytics_api = async (
  brandId: number
): Promise<BrandContentAnalytics | null> => {
  try {
    const response = await Axios.get<BrandContentAnalytics>(`/brand-content/${brandId}/analytics`, {
      timeout: 60 * 1000
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching brand content analytics:', error);
    return null;
  }
}

/**
 * Retrieves demographic data for brand content presaves
 * @param brand - Brand identifier
 * @param name - Content name identifier
 * @returns Demographics data if successful, null otherwise
 */
export const getPresaveDemographics_api = async (
  brand: string,
  name: string
): Promise<any | null> => {
  try {
    const response = await Axios.get<any>(`/brand-content/${brand}/${name}/presaves/demographics`);
    return response.data;
  } catch (error) {
    console.error('Error fetching presave demographics:', error);
    return null;
  }
}

/**
 * Fetches daily presave statistics for brand content
 * @param brand - Brand identifier
 * @param name - Content name identifier
 * @param startDate - Optional start date filter
 * @param endDate - Optional end date filter
 * @returns Daily presave stats if successful, null otherwise
 */
export const getPresaveDailyStats_api = async (
  brand: string,
  name: string,
  startDate?: string | null,
  endDate?: string | null
): Promise<any | null> => {
  try {
    const userTimezone = dayjs.tz.guess();
    const response = await Axios.get<any>(`/brand-content/${brand}/${name}/presaves/daily`, {
      params: {
        startDate,
        endDate,
        timezone: userTimezone
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching daily presave stats:', error);
    return null;
  }
}

/** Interface for presave audience response */
interface PresaveAudienceResponse {
  count: number;
  audience: AudienceMember[];
}

/**
 * Retrieves audience data for brand content presaves
 * @param brand - Brand identifier
 * @param name - Content name identifier
 * @param currentOrPreviousRelease - Whether to fetch current or previous release data
 * @param limit - Number of records to return
 * @param offset - Number of records to skip
 * @param orderBy - Field to order by
 * @param direction - Sort direction
 * @returns Audience data if successful, null otherwise
 */
export const getPresaveAudience_api = async (
  brand: string,
  name: string,
  currentOrPreviousRelease: 'current' | 'previous',
  limit: number,
  offset: number,
  orderBy?: string,
  direction?: string
): Promise<any | null> => {
  try {
    const response = await Axios.get<any>(`/brand-content/${brand}/${name}/presaves/audience`, {
      params: {
        currentOrPreviousRelease,
        limit,
        offset,
        orderBy,
        direction
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching presave audience:', error);
    return null;
  }
}

/**
 * Gets geographical statistics for brand content presaves
 * @param brand - Brand identifier
 * @param name - Content name identifier
 * @param type - Geography type (e.g. 'country', 'city')
 * @returns Geography stats if successful, null otherwise
 */
export const getPresaveGeographies_api = async (
  brand: string,
  name: string,
  type: 'country' | 'city' = 'country'
): Promise<any | null> => {
  try {
    const response = await Axios.get<any>(
      `/brand-content/${brand}/${name}/presaves/geographies/${type}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching presave geographies:', error);
    return null;
  }
}

/** Interface for link update request */
interface LinkUpdateRequest {
  serviceName: string;
  url: string;
}

/** Interface for link update response */
interface LinkUpdateResponse {
  success: boolean;
  message?: string;
  data?: any;
}

/**
 * Updates or creates links for brand content
 * @param brandSlug - Brand slug identifier
 * @param recordSlug - Record slug identifier 
 * @param links - Link data to update
 * @returns Updated link data if successful, null otherwise
 */
export const updateBrandContentLinks_api = async (
  brandSlug: string,
  recordSlug: string,
  links: LinkUpdateRequest
): Promise<LinkUpdateResponse | null> => {
  try {
    const results = await Axios.post<LinkUpdateResponse>(`/brand-content/${brandSlug}/${recordSlug}/link`, links);
    return results.data;
  } catch (error) {
    console.error('Error updating brand content links:', error);
    return null;
  }
}

/**
 * Refreshes brand content for a brand
 * @param brandId - ID of the brand
 * @returns Success status if successful, null otherwise
 */
export const refreshBrandContent_api = async (
  brandId: number
): Promise<{ success: boolean } | null> => {
  try {
    const response = await Axios.post<{ success: boolean }>(`/brand-content/${brandId}/refresh`);
    return response.data;
  } catch (error) {
    console.error('Error refreshing brand content:', error);
    return null;
  }
}